import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { CoRegulateMark } from '../components/Logo';
import { PrimaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { BrandInput } from '../components/brand/BrandInput';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await axios.post(`${API}/api/auth/forgot-password`, { email });
            setEmailSent(true);
            toast.success('Reset link sent! Check your email.');
        } catch (err) {
            // Don't reveal if email exists or not for security
            setEmailSent(true);
            toast.success('If an account exists with this email, you will receive a reset link.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4] flex flex-col">
            {/* Back Button */}
            <div className="p-4">
                <button
                    onClick={() => navigate('/auth?mode=login')}
                    className="flex items-center gap-2 text-[#1D6B6B] hover:text-[#145555] transition-colors"
                    data-testid="back-to-login"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-sm font-medium">Back to sign in</span>
                </button>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center px-4 pb-12">
                <div className="glass-card rounded-2xl p-5">
                    <div className="flex flex-col items-center mb-8">
                        <CoRegulateMark size={48} />
                        <Heading level={2} className="mt-4 text-center">
                            {emailSent ? 'Check your email' : 'Reset your password'}
                        </Heading>
                        <BodyText className="text-center mt-2 text-[#7D8E95]">
                            {emailSent 
                                ? "We've sent you a link to reset your password"
                                : "Enter your email and we'll send you a reset link"
                            }
                        </BodyText>
                    </div>

                    {emailSent ? (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-[#1D6B6B]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle className="w-8 h-8 text-[#1D6B6B]" />
                            </div>
                            <BodyText className="mb-6">
                                Check your inbox at <strong>{email}</strong> for the reset link.
                            </BodyText>
                            <SmallText className="text-[#7D8E95] mb-6">
                                Didn't receive it? Check your spam folder or try again.
                            </SmallText>
                            <button
                                onClick={() => setEmailSent(false)}
                                className="text-[#1D6B6B] hover:text-[#145555] transition-colors text-sm font-medium"
                            >
                                Try a different email
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <BrandInput
                                id="email"
                                name="email"
                                type="email"
                                label="Email address"
                                placeholder="your@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                icon={<Mail className="w-5 h-5" />}
                                data-testid="forgot-email-input"
                            />

                            <PrimaryButton
                                type="submit"
                                className="w-full mt-6"
                                size="xl"
                                disabled={loading}
                                data-testid="send-reset-link-btn"
                            >
                                {loading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    'Send reset link'
                                )}
                            </PrimaryButton>
                        </form>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="text-center py-4">
                <SmallText className="text-[#B0A8A0]">
                    Remember your password?{' '}
                    <a 
                        href="/auth?mode=login" 
                        className="text-[#1D6B6B] hover:text-[#145555] transition-colors"
                    >
                        Sign in
                    </a>
                </SmallText>
            </div>
        </div>
    );
};

export default ForgotPassword;
